import React, { useEffect, useRef } from 'react';
import {
  isShowHidden,
  isShowHiddenFeature,
  isSilenceToaster,
  FEATURE_FLAGS,
  TENANT_TYPES,
  LOGS,
  sendLogToKibana,
  useFeatureHubData,
  useWhiteLabelConfig
} from '@cyberint/common-utils';
import { useSnackbars, Button } from '@cyberint/dsc';
import { t } from '../i18n';
import * as S from './styled';

const CLICKED_VALUE = 'true';

/* Feature Hub bannerContent example:
[
  {
    "key": "banner_roadmap_webinar_2026_Apr",
    "startFrom": "2026-04-30T09:00:00Z", // Optional
    "validUntil": "2026-05-17T09:00:00Z",
    "message": "Our Product Roadmap webinar is coming up on May 7th",
    "linkText": "Register",
    "linkUrl": "https://checkpoint.zoom.us/webinar/register/WN_pbIdIm05RGmrXmLIhtP5bg",
    "whiteLabel": true, // false or empty - will not show for WL env
    "tenantTypes": [ // if empty - visible for all
      "cyberint_customer",
      "cyberint_management",
      "cyberint_provisioning",
      "mssp_standalone_allocated",
      "mssp_parent_allocated",
      "mssp_management",
      "mssp_provisioning",
      "mssp_report"
    ]
  },
  { // minimal
    "key": "banner_roadmap_webinar_2026_Apr",
    "validUntil": "2026-05-17T09:00:00Z",
    "message": "Our Product Roadmap webinar is coming up on May 7th", // link will be register
    "linkUrl": "https://checkpoint.zoom.us/webinar/register/WN_pbIdIm05RGmrXmLIhtP5bg"
  }
]
*/

// Hoisted from apps/ui/src/hooks/useFeedback.js so the shell can run it too.
// `user` is injected (apps/ui passes RootContext userInfo, the shell its session atom).
export const useFeedbackBanners = user => {
  const { isWhiteLabeledCompany } = useWhiteLabelConfig();
  const { infoSnackbar, warningSnackbar, closeNotification } = useSnackbars();
  const clickedKeys = useRef(new Set());
  const { flags } = useFeatureHubData();
  const bannerContent = flags?.[FEATURE_FLAGS.BANNER_DATA];
  const customer = user?.customer?.id || user?.customer?._id;
  const tenantType = user?.customer?.tenant_type;
  const userName = user?.username;

  useEffect(() => {
    if (!userName || !bannerContent || isSilenceToaster()) return;

    const handleFeedback = ({ key, startFrom, validUntil, message, linkText, linkUrl, whiteLabel, tenantTypes }) => {
      // in case of show hidden we override visibility checks
      if (!isShowHidden()) {
        if (isWhiteLabeledCompany && whiteLabel !== true) return; // will not show for WL unless explicitly set to true
        if (tenantTypes?.length && !tenantTypes.includes(tenantType)) return; // if specified, then logged-in tenant type must resolve
      }

      if (
        (startFrom && Date.now() < new Date(startFrom)) ||
        Date.now() > new Date(validUntil) ||
        window.localStorage.getItem(key) === CLICKED_VALUE
      ) {
        return;
      }

      const log = msg => {
        sendLogToKibana({
          logLevel: LOGS.LOG_LEVEL_INFO,
          template: msg,
          params: [message, userName, customer],
          rootLevel: { user: userName, customer },
          extraParams: { customer_id: customer, user_email: userName }
        });
      };

      const onClose = () => {
        if (!clickedKeys.current.has(key)) {
          window.localStorage.setItem(key, CLICKED_VALUE);
          log(`Banner message "%s" was closed by: %s under customer: %s'`);
        }
      };

      let snackbarKey;
      const onClick = () => {
        clickedKeys.current.add(key);
        if (snackbarKey) {
          closeNotification(snackbarKey);
        }

        window.localStorage.setItem(key, CLICKED_VALUE);
        try {
          new URL(linkUrl).protocol === 'https:' && window.open(linkUrl, '_blank', 'noopener,noreferrer');
          log(`Banner message "%s" was clicked by: %s under customer: %s'`);
        } catch {
          log(`Banner message "%s" (or link) is compromised. Click tried by: %s under customer: %s'`);
        }
      };

      snackbarKey = infoSnackbar(
        <S.MessageButtonWrapper>
          {message}
          {!!linkUrl && <Button.Linked onClick={onClick}>{linkText || t('feedback.link')}</Button.Linked>}
        </S.MessageButtonWrapper>,
        { autoHideDuration: null, preventDuplicate: true, onClose }
      );
    };

    try {
      const parsed = JSON.parse(bannerContent);
      const banners = Array.isArray(parsed) ? parsed : [parsed];
      banners.forEach(handleFeedback);
    } catch (e) {
      isShowHiddenFeature() && warningSnackbar('[Visible for Dev  Only] Error parsing banner content');
    }

    handleFeedback({
      key: 'banner_survey_2026_Jun',
      validUntil: '2026-07-01T18:00:00Z',
      message: t('survey.title'),
      linkText: t('survey.clickhere'),
      tenantTypes: [TENANT_TYPES.CYBERINT_CUSTOMER],
      linkUrl: `https://cyberint.typeform.com/to/Ffd1ouH5?utm_campaign=argos&email=${userName}`
    });
  }, [bannerContent, isWhiteLabeledCompany, userName]);
};
